import _, { isFinite } from 'lodash';
import {
  SPACE_NAME_MAP,
  GROWTH_CALCULATORS,
  WORKSHEET_VALUES,
  SHARED_PRIVATE_OFFICE,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  CUSTOM_SUBCATEGORY,
  CUSTOM_SUPPORT_SPACE_TYPE,
} from 'spw-constants';

import {
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import {
  Scenario,
  IWorksheetRow,
  IWorksheet,
  IWorksheetRowValue,
  IChildWorksheetSpaceType,
  CalculatorType,
} from 'spw-redux/modules/calculators/types';
import * as R from 'ramda';
import React, { ChangeEvent } from 'react';
import { limitToCharLength } from 'spw-shared/utils';
import {
  Category,
  IProjectSpaceType,
  ISpaceType,
} from 'spw-redux/modules/space_types/types';
import { sortRowsByPreviousId } from 'spw-redux/modules/calculators/utils';
import {
  isCustomSubcategory,
  isIWCategoryOrSubcategory,
} from 'spw-projects/utils';
import { defaultSubcategoryNames } from 'spw-components/dialogs/update-space-category';
import WorksheetRow from '../../models/WorksheetRow';
import { PlainProgramScenario } from '../../models/Scenario';

export const DEFAULT_INDIVIDUAL_NAME = 'Untitled Space';
export const DEFAULT_DUPLICATE_CUSTOM_SPACE = 'Duplicate Space Type Name';
const { SUPPORT_SPACES } = SPACE_NAME_MAP;

export const isMacro = (scenario: Scenario | PlainProgramScenario) => {
  if (scenario) {
    const type = scenario.calculatorType?.replace(/_/g, '-');
    return type === GROWTH_CALCULATORS.macroProgramming;
  }
  return false;
};

export const isDepartmentScenario = (
  scenario: Scenario | PlainProgramScenario
) => {
  return (
    scenario?.calculatorType?.replace(/_/g, '-') ===
    GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments
  );
};

export const isFloorScenario = (calculatorType: CalculatorType) => {
  return [
    GROWTH_CALCULATORS.floorHeadcountSeatcount,
    GROWTH_CALCULATORS.floorUsf,
  ].includes(calculatorType);
};

export const isHCSCScenario = (calculatorType: CalculatorType) => {
  return [
    GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments,
    GROWTH_CALCULATORS.buildingHeadcountSeatcount,
    GROWTH_CALCULATORS.floorHeadcountSeatcount,
  ].includes(calculatorType);
};

export const getQtyFromRatio = (ratio: number, totalWorkSeats: number) =>
  !_.isNaN(totalWorkSeats / ratio) && _.isFinite(totalWorkSeats / ratio)
    ? totalWorkSeats / ratio
    : 0;

export const getRatioFromQty = (qty: number, totalWorkSeats: number) =>
  !_.isNaN(totalWorkSeats / qty) && _.isFinite(totalWorkSeats / qty)
    ? totalWorkSeats / qty
    : 0;

export const defaultRowValues: IWorksheetRow = {
  quantity: 0,
  sizeX: 0,
  sizeY: 0,
  usf: 0,
  ratio: 0,
  removed: false,
  seatCap: 0,
  spaceType: null,
  category: {
    name: 'Individual Workspaces',
    icon: 'individual-workspaces-icon',
    defaultSpaceTypeCategory: {
      name: 'Individual Workspaces',
      category: INDIVIDUAL_WORKSPACES_CATEGORY,
    },
    subcategories: [
      {
        defaultSpaceTypeCategory: {
          name: 'Workstations',
          category: 'workstations',
        },
      },
      {
        defaultSpaceTypeCategory: {
          name: 'Private Offices',
          category: 'private_offices',
        },
      },
    ],
  } as IProjectSpaceTypeCategory,
};

export const isStorageSupportSpace = (spaceType: ISpaceType) =>
  _.includes(
    [SUPPORT_SPACES.STORAGE_LOCKER, SUPPORT_SPACES.FILE_CABINET],
    spaceType?.spaceType
  ) ||
  (spaceType?.spaceType === CUSTOM_SUPPORT_SPACE_TYPE &&
    /^\d+ High ((Lateral|Vertical) (File Cabinets|Lockers))$/.test(
      spaceType?.name
    ));

export const shouldDisplayAsRatio = (
  projectSpaceTypeCategory: IProjectSpaceTypeCategory,
  projectSpaceTypeSubcategory?: IProjectSpaceTypeSubcategory
) => {
  if (
    projectSpaceTypeSubcategory &&
    projectSpaceTypeSubcategory.defaultSpaceTypeCategory.category ===
      CUSTOM_SUBCATEGORY &&
    !projectSpaceTypeSubcategory.includeInTotalWorkSeats
  )
    return true;
  return !isIWCategoryOrSubcategory(projectSpaceTypeCategory);
};

export const findCategory = (
  projectSpaceTypeCategories: IProjectSpaceTypeCategory[],
  defaultSpaceTypeCategory: string
) => {
  return projectSpaceTypeCategories?.find(
    (projectSpaceTypeCategory: IProjectSpaceTypeCategory) =>
      projectSpaceTypeCategory.defaultSpaceTypeCategory.category ===
      defaultSpaceTypeCategory
  );
};

export const getProjectSpaceTypeCategoriesMap = (
  projectSpaceTypeCategories: IProjectSpaceTypeCategory[]
) => {
  const categoriesMap: Partial<Record<
    Category,
    IProjectSpaceTypeCategory
  >> = {};
  if (projectSpaceTypeCategories?.length)
    projectSpaceTypeCategories.forEach((c) => {
      categoriesMap[c.defaultSpaceTypeCategory.category as Category] = c;
    });
  return categoriesMap;
};

export const isWorkCategory = (r: IWorksheetRow | { category: string }) =>
  r?.category === 'privateOffices' || r?.category === 'individualWorkspaces';

export const onlyAllowNumbersFromInput = (
  e: React.ChangeEvent<HTMLInputElement>
) => e.target.value.replace(/[^0-9]/g, '');

// test case in utils.test.tsx
export const sharedMacroSubtotalUsf = (
  individualWorkspaceUsf: number,
  sharedSpaceAllocation: number
) => {
  const denom = 100 - sharedSpaceAllocation;
  if (denom <= 0) {
    return 0;
  }
  return (individualWorkspaceUsf * sharedSpaceAllocation) / denom;
};

export const getRowRatio = (row: IWorksheetRow, totalWorkSeats: number) =>
  shouldDisplayAsRatio(row.category, row.subcategory)
    ? getRatioFromQty(row.quantity, totalWorkSeats)
    : (100 * row.quantity * row.seatCap) / totalWorkSeats;

export const updateWorksheetRows = (
  worksheet: IWorksheet,
  totalWorkSeats: number,
  sharedSpacesFieldsToKeep: { [key: string]: IWorksheetRowValue },
  workspacesFieldToKeep?: IWorksheetRowValue,
  forceQuantityFix?: boolean,
  oldTotalWorkSeats?: number
) => {
  return worksheet?.rows?.map((row: IWorksheetRow) => {
    const isWorkCat = isIWCategoryOrSubcategory(row.category);

    const fieldToKeep = isWorkCat
      ? workspacesFieldToKeep
      : sharedSpacesFieldsToKeep?.[
          row.category.defaultSpaceTypeCategory.category
        ];

    if (
      !fieldToKeep ||
      fieldToKeep === WORKSHEET_VALUES.QUANTITY_OF_SPACE ||
      (forceQuantityFix && !isWorkCat)
    ) {
      return {
        ...row,
        ratio: getRowRatio(row, totalWorkSeats),
      };
    }

    const rowRatio = oldTotalWorkSeats
      ? getRowRatio(row, oldTotalWorkSeats || totalWorkSeats)
      : row.ratio;
    const isCustomSubcategoryNotIncludedInWS =
      row.subcategory?.defaultSpaceTypeCategory.category ===
        CUSTOM_SUBCATEGORY && !row.subcategory.includeInTotalWorkSeats;

    if (!isWorkCat || isCustomSubcategoryNotIncludedInWS) {
      return {
        ...row,
        quantity: getQtyFromRatio(rowRatio, totalWorkSeats),
      };
    }

    const workSeats = (totalWorkSeats * rowRatio) / 100;

    const newQuantity = workSeats / row.seatCap;

    return {
      ...row,
      quantity: isFinite(newQuantity) ? newQuantity : 0,
    };
  });
};

export const defaultChildName = DEFAULT_INDIVIDUAL_NAME;

export const isDuplicateCategory = (
  spaceTypeCategories: IProjectSpaceTypeCategory[],
  subCategories: IProjectSpaceTypeSubcategory[],
  id: number,
  newName: string
) => {
  const lowerCaseName = newName.trim().toLowerCase();

  const nameIsCategoryDuplicate = spaceTypeCategories.some(
    (cat: IProjectSpaceTypeCategory) => {
      const nameIsDuplicated = cat.name?.toLowerCase().trim() === lowerCaseName;

      return nameIsDuplicated && id !== cat.id;
    }
  );

  const nameIsSubcategoryDuplicate = subCategories.some(
    (projectSubcategory) => {
      if (
        projectSubcategory.name === '' &&
        !isCustomSubcategory(projectSubcategory)
      ) {
        return (
          lowerCaseName ===
            defaultSubcategoryNames[
              projectSubcategory.defaultSpaceTypeCategory.category
            ].toLowerCase() && projectSubcategory.id !== id
        );
      }

      if (projectSubcategory.name.trim() && lowerCaseName) {
        return (
          projectSubcategory.name.trim().toLowerCase() === lowerCaseName &&
          projectSubcategory.id !== id
        );
      }

      return false;
    }
  );

  return nameIsSubcategoryDuplicate || nameIsCategoryDuplicate;
};

export const isDuplicateSpace = (
  projectSpaceTypes: IProjectSpaceType[],
  childWorksheetSpaces: IChildWorksheetSpaceType[],
  name: string,
  id?: number
) => {
  const regex = / |\n/gm;

  const lowerCaseName = name?.toLowerCase()?.trim().replace(regex, '');

  if (
    lowerCaseName === DEFAULT_INDIVIDUAL_NAME.toLowerCase().replace(regex, '')
  )
    return false;

  const nameIsProjectSpaceTypeDuplicate = projectSpaceTypes.some(
    (projectSpaceType: IProjectSpaceType) => {
      const nameIsDuplicated =
        projectSpaceType.spaceType.name?.toLowerCase().replace(regex, '') ===
          lowerCaseName ||
        lowerCaseName ===
          projectSpaceType.clientFacingName?.toLowerCase().replace(regex, '');

      return nameIsDuplicated && projectSpaceType.spaceType.id !== id;
    }
  );
  const nameIsChildSpaceDuplicate = childWorksheetSpaces.some(
    (childWorksheetSpace: IChildWorksheetSpaceType) => {
      const nameIsInternalNameDuplicate =
        childWorksheetSpace?.name?.toLowerCase()?.replace(regex, '') ===
        lowerCaseName;
      const nameIsClientNameDuplicate =
        childWorksheetSpace?.clientFacingName
          ?.toLowerCase()
          .replace(regex, '') === lowerCaseName;

      return (
        (nameIsInternalNameDuplicate || nameIsClientNameDuplicate) &&
        childWorksheetSpace.id !== id
      );
    }
  );

  return nameIsProjectSpaceTypeDuplicate || nameIsChildSpaceDuplicate;
};

export const uniqueRow = (r: IWorksheetRow | WorksheetRow) => {
  return `${r?.worksheetId ? `${r.worksheetId}-` : ''}${
    r.spaceType?.id
  }-${!!r.child}-${!!r.removed}`;
};

export const limitInputValue = (
  event: React.ChangeEvent<HTMLInputElement>,
  maxValue: number
) =>
  R.pipe(
    (value: ChangeEvent<HTMLInputElement>) => onlyAllowNumbersFromInput(value),
    (value: string) => limitToCharLength(value, maxValue)
  )(event);

export const generatedPreNamedSpaces = [
  ..._.times(10, null).map((v: null, i: number) => `${i + 1}H Lockers`),
  ..._.times(10).map((i: number) => `${i + 1}H Lateral File Cabinets`),
  ..._.times(10).map((i: number) => `${i + 1}H Vertical File Cabinets`),
];

export const isPreNamedSpace = (newName: string) =>
  generatedPreNamedSpaces.includes(newName);

export const getRowSpaceType = (
  row: IWorksheetRow,
  rowLinkToSpaceType: IChildWorksheetSpaceType | IProjectSpaceType
) => {
  if (row.child) {
    if (rowLinkToSpaceType)
      return {
        ...rowLinkToSpaceType,
        spaceType: (rowLinkToSpaceType as IChildWorksheetSpaceType)?.parent
          ?.spaceType,
      };
    if (row?.spaceType?.spaceType) return row.spaceType;
  }

  return {
    ...(rowLinkToSpaceType as IProjectSpaceType)?.spaceType,
    spaceTypeCategory: (rowLinkToSpaceType as IProjectSpaceType)?.category,
  };
};

export const removeRowFromWorksheet = (
  row: WorksheetRow,
  rows: WorksheetRow[] = []
) => {
  const rowIndex = rows.findIndex((r) => r.id === row.id);

  const newRows = rows.slice();
  newRows.splice(rowIndex, 1, {
    ...row,
    quantity: 0,
    seatCap: 0,
    ratio: 0,
    sizeX: 0,
    sizeY: 0,
    usf: 0,
    removed: true,
  });
  if (!row.child) {
    const childRowsToBeRemoved = newRows.filter((r) => {
      return r.spaceType?.parent?.id === row.spaceType?.id;
    });

    childRowsToBeRemoved.forEach((childRowToRemove: WorksheetRow) => {
      const childRowIndex = newRows.findIndex(
        (r) => r.id === childRowToRemove.id
      );
      newRows.splice(childRowIndex, 1, {
        ...childRowToRemove,
        quantity: 0,
        seatCap: 0,
        ratio: 0,
        sizeX: 0,
        sizeY: 0,
        usf: 0,
        removed: true,
      });
    });
  }

  return newRows;
};

export const generateSortingAfterDrag = ({
  categorySpaceRows,
  newIndex,
  rowProjectSpaceTypeId,
}: {
  categorySpaceRows: IWorksheetRow[];
  newIndex: number;
  rowProjectSpaceTypeId: number;
}) => {
  let rows = categorySpaceRows.filter(
    (r) => !isStorageSupportSpace(r.projectSpaceType.spaceType)
  );

  rows = sortRowsByPreviousId(rows);

  const notRemovedRows = rows.filter((r) => {
    return !r.removed;
  });

  // Get the id of the row that occupies the new position
  const destinationRowId = notRemovedRows[newIndex]?.id;

  // Look for the index of the destination row in the rows list
  const destinationIndex = rows.findIndex((r) => r.id === destinationRowId);

  if (destinationIndex === -1) {
    return { newOrderedRows: rows, projectSpaceTypesToUpdate: [] };
  }

  let newOrderedRows = rows.slice();
  const projectSpaceTypesToUpdate: IProjectSpaceType[] = [];
  const oldIndex = _.findIndex(
    rows,
    ({ projectSpaceType: { id } }) => id === rowProjectSpaceTypeId
  );

  newOrderedRows.splice(
    destinationIndex,
    0,
    newOrderedRows.splice(oldIndex, 1)[0]
  );

  let previousSpaceTypeId: null | number = null;

  newOrderedRows = newOrderedRows.map((r, i) => {
    const oldRow = rows[i];
    const newProjectSpaceType = {
      ...r.projectSpaceType,
      previousId: previousSpaceTypeId,
    };

    if (oldRow?.projectSpaceType !== newProjectSpaceType) {
      projectSpaceTypesToUpdate.push(newProjectSpaceType as IProjectSpaceType);
    }
    previousSpaceTypeId = r?.projectSpaceType?.id;
    return { ...r, projectSpaceType: newProjectSpaceType };
  });
  return {
    newOrderedRows,
    projectSpaceTypesToUpdate: newOrderedRows.map((r) => r.projectSpaceType),
  };
};

/**
 * Creates a new map from child worksheet spaces.
 * @param {IChildWorksheetSpaceType[]} childWorksheetSpaces - List of child worksheet spaces
 * @returns { {[key: number]: IChildWorksheetSpaceType} }
 * */
export const getChildSpaceTypesMap = (
  childWorksheetSpaces: IChildWorksheetSpaceType[]
) => {
  return childWorksheetSpaces.reduce(
    (map: { [key: number]: IChildWorksheetSpaceType }, childSpaceType) => {
      if (childSpaceType?.id) {
        return {
          ...map,
          [childSpaceType.id]: childSpaceType,
        };
      }
      return map;
    },
    {}
  );
};

// TODO: Use project space type category
export const defaultRowValuesWithSeatCap = (
  spaceType: ISpaceType
): IWorksheetRow => {
  let seatCap = 0;
  if (isWorkCategory({ category: spaceType.spaceTypeCategory?.category })) {
    seatCap = spaceType.spaceType === SHARED_PRIVATE_OFFICE ? 2 : 1;
  }
  return {
    ...defaultRowValues,
    seatCap,
  };
};

export const buildNewRow = (projectSpaceType: IProjectSpaceType) => {
  let seatCap = 0;

  if (isIWCategoryOrSubcategory(projectSpaceType.category)) {
    seatCap =
      projectSpaceType.spaceType.spaceType === SHARED_PRIVATE_OFFICE ? 2 : 1;
  }

  return {
    ...defaultRowValues,
    seatCap,
    projectSpaceType,
    spaceType: projectSpaceType.spaceType,
    spaceTypeId: projectSpaceType.id,
    child: false,
  };
};

export const buildNewChildRow = (
  parentSpaceType: IProjectSpaceType,
  childSpaceType: IChildWorksheetSpaceType
) => {
  let seatCap = 0;
  if (isIWCategoryOrSubcategory(parentSpaceType.category)) {
    seatCap =
      parentSpaceType.spaceType.spaceType === SHARED_PRIVATE_OFFICE ? 2 : 1;
  }

  return {
    ...defaultRowValues,
    seatCap,
    spaceType: childSpaceType,
    projectSpaceType: parentSpaceType,
    spaceTypeId: childSpaceType.spaceTypeId,
    child: true,
  };
};
